import React, { Component } from 'react';
import PropTypes from 'prop-types';
import BEMHelper from '@ynap/bem-helper';
import { Stepper } from '@ynap/stepper';
import LiteLayout from '../Lite';
import ColoursWithState from '../../Colours/ColoursWithState';
import SizeSelect from '../../SizeSelect/SizeSelect';
import PartNumber from '../../PartNumber/PartNumber';
import CTAButtons from '../../CTAButtons/CTAButtons';
import { redirectToCheckout, redirectToWishlist } from '@ynap/redirect-utils';
import { withAnalytics, safeBuildEventObj } from '@ynap/analytics';
import track, { TrackingPropType } from 'react-tracking';
import {
    trackAddToBagFromMiniPDP,
    trackAddToWishlistFromMiniPDP,
    trackViewMoreDetails,
    trackAddRemoveItem,
    trackAddToBagError,
    trackAddToWishlistError,
    trackGoToBag,
    trackAddToBagNonBasketProduct,
} from '../../analytics';
import { SingleBadge } from '@ynap/badge';
import { withOptimizely } from '@ynap/optimizely-utils';
import Notification, { Tooltip } from '@ynap/notification';

const bem = new BEMHelper('QuickViewProductDetailsPage');

@track()
class QuickViewProductDetailsPageLayout extends Component {
    static propTypes = {
        swatchHorizontalScroll: PropTypes.bool,
        product: PropTypes.object.isRequired,
        productUrl: PropTypes.string.isRequired,
        wishlistUrl: PropTypes.string,
        selectedProductVariant: PropTypes.object.isRequired,
        selectedSku: PropTypes.object,
        selectedSizeLabel: PropTypes.string.isRequired,
        messages: PropTypes.shape({
            colourAndSize: PropTypes.shape({
                colour: PropTypes.func.isRequired,
            }).isRequired,
            product: PropTypes.shape({
                productCode: PropTypes.func.isRequired,
                viewMoreDetails: PropTypes.func.isRequired,
            }).isRequired,
        }),
        locale: PropTypes.string.isRequired,
        addToCart: PropTypes.func.isRequired,
        addToCartLoading: PropTypes.bool,
        addToWishlist: PropTypes.func.isRequired,
        addToWishlistLoading: PropTypes.bool,
        wishlists: PropTypes.array,
        selectProduct: PropTypes.func.isRequired,
        quantity: PropTypes.shape({
            initialQuantity: PropTypes.number.isRequired,
            minQuantity: PropTypes.number,
            maxQuantity: PropTypes.number,
        }),
        wishlistId: PropTypes.string.isRequired,
        loading: PropTypes.bool,
        closeModal: PropTypes.func,
        errors: PropTypes.shape({
            addToCart: PropTypes.string,
            addToWishlist: PropTypes.string,
        }),
        multipleWishlistsFeatureToggle: PropTypes.bool,
        sizeGridFeatureToggle: PropTypes.bool,
        analytics: PropTypes.shape({
            category: PropTypes.shape({
                pageType: PropTypes.string.isRequired,
                subCategory1: PropTypes.string.isRequired,
            }),
        }),
        tracking: TrackingPropType,
        urlConfig: PropTypes.object,
        disabledControls: PropTypes.bool,
        setModal: PropTypes.func,
        consideredBadge: PropTypes.string,
        optimizely: PropTypes.shape({
            track: PropTypes.func,
        }),
        config: PropTypes.object,
        hidePrice: PropTypes.bool,
        isRecommendation: PropTypes.bool,
        cartLineItem: PropTypes.object,
        showMinimumPriceLabel: PropTypes.bool,
        isWishlist: PropTypes.bool,
        onAddToCartSuccess: PropTypes.func,
    };

    constructor(props) {
        super(props);
        this.state = {
            quantity: props.quantity ? props.quantity.initialQuantity : 1,
            noSizeNotification: null,
            toggleRectificationMsg: false,
        };

        this.trackedAddToBag = this.trackedAddToBag.bind(this);
        this.trackedAddToWishlistError = this.trackedAddToWishlistError.bind(this);
        this.trackedAddToWishlist = this.trackedAddToWishlist.bind(this);
        this.trackedAddToBagError = this.trackedAddToBagError.bind(this);
        this.trackedGoToBag = this.trackedGoToBag.bind(this);
        this.safeTrackEvent = this.safeTrackEvent.bind(this);
    }

    setNoSizeNotification = (noSizeNotification) => {
        this.setState({ noSizeNotification });
    };

    safeTrackEvent(buildAnalyticsFunc, ...args) {
        const { analytics, tracking } = this.props;
        tracking.trackEvent(safeBuildEventObj(buildAnalyticsFunc, analytics, ...args));
    }

    trackedAddToWishlist(sku, wishlistId) {
        const { addToWishlist, selectedProductVariant } = this.props;
        const { colourPartNumber } = selectedProductVariant;
        return addToWishlist(sku, wishlistId)
            .then(() => {
                return this.safeTrackEvent(trackAddToWishlistFromMiniPDP, sku, colourPartNumber);
            })
            .catch((error) => {
                return this.safeTrackEvent(trackAddToWishlistError, error.type);
            }); // TODO Add raygun call for catch addToWishlist error.
    }

    trackedAddToWishlistError() {
        this.safeTrackEvent(trackAddToWishlistError, 'NO_SIZE_SELECTED');
    }

    trackedAddToBag(sku, quantity, selectedProductVariant, onAddToCartSuccess) {
        const { addToCart, optimizely, isRecommendation = false, config } = this.props;
        const includeShoppingCart = config?.head?.wishlist?.includeShoppingCart;
        return addToCart(sku, { includeShoppingCart, quantity })
            .then((response) => {
                if (response?.toggleRectificationMsg) {
                    const { toggleRectificationMsg } = response;
                    this.setState({ toggleRectificationMsg });
                }
                if (response && response.type === 'ADD_TO_CART_SUCCESS') {
                    onAddToCartSuccess?.();
                    isRecommendation && optimizely?.track('quickView_addToCart');
                    return isRecommendation
                        ? this.safeTrackEvent(trackAddToBagNonBasketProduct, selectedProductVariant, sku, 'recommendation')
                        : this.safeTrackEvent(trackAddToBagFromMiniPDP, quantity);
                } else {
                    return this.safeTrackEvent(trackAddToBagError, response.type);
                }
            })
            .catch((error) => {
                return this.safeTrackEvent(trackAddToWishlistError, error.type);
                //add these to raygun (ISPW-415)
            }); // TODO Add raygun call for catch addToCart error.
    }

    trackedAddToBagError() {
        this.safeTrackEvent(trackAddToBagError, 'NO_SIZE_SELECTED');
    }

    trackedGoToBag(locale) {
        this.safeTrackEvent(trackGoToBag);
        return redirectToCheckout(locale);
    }

    componentDidUpdate(prevProps) {
        let maxQuantity;
        // ensure current quantity does not exceed maximum
        if (this.props.cartLineItem?.designerCartItemLimit) {
            maxQuantity = Math.min(this.props.cartLineItem.designerCartItemLimit, this.props.quantity?.maxQuantity);
        } else {
            maxQuantity = this.props.quantity?.maxQuantity;
        }

        if (maxQuantity && this.state.quantity > maxQuantity) {
            this.setState({
                quantity: this.props.quantity.maxQuantity,
            });
        }

        // only update if quantity is intended to be displayed
        if (
            (!prevProps.quantity && this.props.quantity) ||
            (prevProps.quantity && this.props.quantity && prevProps.quantity.initialQuantity !== this.props.quantity.initialQuantity)
        ) {
            this.setState(() => ({
                quantity: this.props.quantity.initialQuantity,
            }));
        }
    }

    @track((props, state, [newQuantity]) => trackAddRemoveItem(props.cartLineItem, state.quantity, newQuantity))
    onQuantityChange = (newQuantity) => this.setState({ quantity: newQuantity });

    render() {
        const {
            product = {},
            consideredBadge,
            productUrl,
            wishlistUrl,
            urlConfig,
            selectedProductVariant = {},
            selectedSku,
            selectedSizeLabel,
            messages,
            locale,
            addToCart,
            addToCartLoading,
            addToWishlist,
            addToWishlistLoading,
            wishlists,
            wishlistId,
            selectProduct,
            quantity,
            loading,
            closeModal,
            multipleWishlistsFeatureToggle,
            sizeGridFeatureToggle,
            errors,
            disabledControls,
            swatchHorizontalScroll,
            config,
            hidePrice = false,
            isRecommendation = false,
            cartLineItem,
            isWishlist,
            country,
            isShoppingBag,
            onAddToCartSuccess,
            selectedProductInfo,
        } = this.props;

        const { toggleRectificationMsg } = this.state;

        const { badges, colourSwatches, colourPartNumber, label, sKUs } = selectedProductVariant;
        let cartButton, wishlistButton;
        if (addToCart) {
            cartButton = {
                addToCart: (partNumber) => this.trackedAddToBag(partNumber, this.state.quantity, selectedProductVariant, onAddToCartSuccess),
                failedAddToBag: this.trackedAddToBagError,
                goToCart: this.trackedGoToBag,
                addToCartLoading,
            };
        }

        if (addToWishlist)
            wishlistButton = {
                addToWishlist: this.trackedAddToWishlist,
                failedAddToWishlist: this.trackedAddToWishlistError,
                addToWishlistLoading,
                wishlists,
                wishlistId,
                goToWishlist: redirectToWishlist,
                wishlistUrl,
            };

        const liteProps = { product, selectedProductVariant, messages, consideredBadge, hidePrice, config, isWishlist, country };
        const isProductSizeDisplayable = !!sKUs?.length;
        const errorNotificationTimer = config?.head?.errorNotificationTimer;
        const includeCartWithAddToBag = config?.head?.pdp?.includeCartWithAddToBag;
        const modifier = { recommendation: isRecommendation };

        return (
            <LiteLayout
                {...liteProps}
                loading={loading}
                displayBadges={true}
                locale={locale}
                withQuickAdd={isRecommendation}
                country={country}
                isShoppingBag={isShoppingBag}
                consideredBadge={selectedProductInfo?.consideredBadge || consideredBadge}
            >
                <div className={bem('gridContainer')}>
                    {!isRecommendation && (
                        <ColoursWithState
                            className={bem('colours')}
                            messages={messages}
                            colours={colourSwatches}
                            colourLabel={label}
                            selectProduct={selectProduct}
                            collapseColours={true}
                            loading={loading}
                            swatchHorizontalScroll={swatchHorizontalScroll}
                        />
                    )}
                    {isProductSizeDisplayable && (
                        <div className={bem('quantityAndSizeWrapper', modifier)}>
                            <SizeSelect
                                className={bem('sizeSelect', { loading })}
                                skus={sKUs}
                                value={selectedSizeLabel}
                                messages={messages}
                                changeSelectedSku={selectProduct}
                                loading={loading}
                                displayLabel={false}
                                numberOfDisplaySize={4}
                                sizeGridFeatureToggle={sizeGridFeatureToggle}
                                disabledControls={disabledControls}
                                noSizeNotification={this.state.noSizeNotification}
                            />

                            {quantity && (
                                <div className={bem('quantityStepper', null, 'quantity')}>
                                    <Stepper
                                        onChange={this.onQuantityChange}
                                        min={0}
                                        max={
                                            cartLineItem?.designerCartItemLimit
                                                ? Math.min(cartLineItem?.designerCartItemLimit, quantity.maxQuantity)
                                                : quantity.maxQuantity
                                        }
                                        orientation="horizontal"
                                        stepperValue={this.state.quantity}
                                        messages={messages}
                                        disabled={!quantity.maxQuantity || disabledControls}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {sizeGridFeatureToggle && badges?.selectedProductSizeBadge && (
                        <SingleBadge className={bem('skuLevelBadge')} data-testid="skuLevelBadge" badge={badges.selectedProductSizeBadge} />
                    )}
                    {this.state.quantity === cartLineItem?.designerCartItemLimit || toggleRectificationMsg ? (
                        <div className={bem('designeLimitNotification')}>
                            <Notification type={'info'} message={messages.basket.designerLimit?.information()} />
                        </div>
                    ) : (
                        ''
                    )}
                    <CTAButtons
                        className={bem('ctaButtons', modifier)}
                        modifier={modifier}
                        messages={messages}
                        cart={cartButton}
                        wishlist={wishlistButton}
                        skuPartNumber={selectedSku && selectedSku.partNumber}
                        selectedProductVariant={selectedProductVariant}
                        closeModal={closeModal}
                        loading={loading}
                        multipleWishlistsFeatureToggle={multipleWishlistsFeatureToggle}
                        errors={errors}
                        isProductSizeDisplayable={isProductSizeDisplayable}
                        numberOfDisplayWishlist={3}
                        urlConfig={urlConfig}
                        locale={locale}
                        setModal={this.props.setModal}
                        disableAddToBag={disabledControls}
                        setNoSizeNotification={this.setNoSizeNotification}
                        errorNotificationTimer={errorNotificationTimer}
                        includeCartWithAddToBag={includeCartWithAddToBag}
                        disableAddToBagSecondaryAction={isRecommendation}
                        preventSkuNotification={config?.head?.pdp?.preventSkuNotification}
                    />
                    {!isRecommendation && (
                        <div className={bem('moreDetails', { loading })}>
                            <PartNumber className={bem('partNumber')} selectedPartNumber={colourPartNumber} messages={messages} loading={loading} />
                            <a
                                className={bem('viewDetails', { loading })}
                                href={productUrl}
                                onClick={() => this.safeTrackEvent(trackViewMoreDetails, colourPartNumber)}
                            >
                                {messages.product.viewMoreDetails()}
                            </a>
                        </div>
                    )}
                </div>
            </LiteLayout>
        );
    }
}

export { QuickViewProductDetailsPageLayout };
export default withAnalytics(withOptimizely(QuickViewProductDetailsPageLayout));
